/**
 * Vincent B677 (Eric 2026-02-XX) — Popover cliquable sur le badge "Enrichi
 * par N vidéos" du dictionnaire. Liste les titres de vidéos + fichiers textes
 * qui ont contribué à la définition consolidée d'un terme.
 *
 * Fetch lazy sur ouverture. Fermeture au clic extérieur / touche Échap.
 */
import { useEffect, useRef } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { X, VideoCamera, FileText, SpinnerGap } from "@phosphor-icons/react";
import { useLang } from "../../contexts/LanguageContext";
import { API_BASE as API } from "../../lib/apiBase";

export default function SourceVideosPopover({ entryId, term, onClose, anchorRect }) {
  const { lang } = useLang();
  const popRef = useRef(null);

  // Vincent B685 (Eric 2026-02-XX) — Migration TanStack Query.
  // Le cache par entryId permet de rouvrir instantanément la popover d'un
  // terme déjà consulté (staleTime 60s).
  const {
    data: sources,
    isPending: loading,
    isError,
    error,
  } = useQuery({
    queryKey: ["jargon", "sources", entryId],
    queryFn: async () => {
      const { data } = await axios.get(`${API}/api/jargon/${entryId}/sources`, {
        withCredentials: true,
      });
      return data;
    },
    staleTime: 60_000,
    enabled: !!entryId,
  });

  // Fermeture automatique en cas d'erreur (comportement de l'ancien code).
  useEffect(() => {
    if (isError) {
      toast.error(error?.response?.data?.detail || (lang === "fr" ? "Erreur" : "Error"));
      onClose?.();
    }
  }, [isError, error, lang, onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    // Delay the click listener to avoid catching the click that opened this popover
    const t = setTimeout(() => document.addEventListener("mousedown", onClick), 50);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
      clearTimeout(t);
    };
  }, [onClose]);

  // Position sous l'ancre (fixed positioning)
  const style = anchorRect
    ? {
        position: "fixed",
        top: Math.min(anchorRect.bottom + 6, window.innerHeight - 320),
        left: Math.min(anchorRect.left, window.innerWidth - 380),
        zIndex: 1000,
      }
    : { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 1000 };

  const videos = sources?.video_sources || [];
  const textDocs = sources?.text_doc_sources || [];
  const total = videos.length + textDocs.length;

  return (
    <div
      ref={popRef}
      style={style}
      className="w-[360px] max-h-[320px] overflow-y-auto bg-white border border-violet-200 rounded-lg shadow-xl"
      data-testid={`source-videos-popover-${entryId}`}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-violet-100 bg-violet-50 sticky top-0">
        <div className="text-xs font-semibold text-violet-900">
          {lang === "fr"
            ? `Sources du terme « ${term} »`
            : `Sources of term "${term}"`}
        </div>
        <button
          onClick={onClose}
          className="text-violet-700 hover:text-violet-900"
          data-testid="close-source-popover"
          aria-label="Close"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
      <div className="p-3">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-6 text-xs text-zinc-500">
            <SpinnerGap size={14} className="animate-spin" />
            {lang === "fr" ? "Chargement…" : "Loading…"}
          </div>
        ) : total === 0 ? (
          <div className="py-4 text-center text-xs text-zinc-500" data-testid="source-videos-empty">
            {lang === "fr"
              ? "Aucune source connue pour ce terme."
              : "No known sources for this term."}
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            {videos.map((v) => (
              <a
                key={v.id}
                href={`/dashboard/videos/${v.id}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-2 px-2 py-1.5 rounded border border-transparent hover:border-violet-200 hover:bg-violet-50 transition-colors"
                data-testid={`source-video-link-${v.id}`}
              >
                {v.is_text_doc ? (
                  <FileText size={13} weight="bold" className="text-violet-600 mt-0.5 flex-shrink-0" />
                ) : (
                  <VideoCamera size={13} weight="bold" className="text-violet-600 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-zinc-900 truncate">{v.title}</div>
                  {v.module_name && (
                    <div className="text-[10px] text-zinc-500 truncate">{v.module_name}</div>
                  )}
                </div>
              </a>
            ))}
            {textDocs.map((d) => (
              <a
                key={d.id}
                href={`/dashboard/videos/${d.id}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-2 px-2 py-1.5 rounded border border-transparent hover:border-violet-200 hover:bg-violet-50 transition-colors"
                data-testid={`source-textdoc-link-${d.id}`}
              >
                <FileText size={13} weight="bold" className="text-violet-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-zinc-900 truncate">{d.title}</div>
                  <div className="text-[10px] text-zinc-500">{lang === "fr" ? "Fichier texte" : "Text file"}</div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
